import useIdentity from "@/features/ui/hooks/use-identity";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Text,
  useToast,
} from "@chakra-ui/react";
import AppLineSheetTypeSelector from "@/features/line-sheet-sets/app-line-sheet-type-selector";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppPagination from "@/features/line-sheet-sets/app-pagination";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { buyerOrderSheetSetRowsMutateId } from "@/features/order-status/recoils/buyer-order-sheet-set-rows-state";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useBuyerOrderSheetSetRows from "@/features/order-status/hooks/use-buyer-order-sheet-set-rows";
import { DEFAULT_LIST_LINE_SHEET_PAIRS_PAGE_SIZE } from "@/features/line-sheet-sets/hooks/use-list-line-sheet-set-pairs";
import BuyerOrderSheetSetGrid from "@/features/order-status/buyer-order-sheet-set-grid";
import AppListHeader from "@/features/ui/app-list-header";
import useListDenseOrderSheetSet from "@/features/order-sheet-sets/hooks/use-list-dense-order-sheet-set";
import { OrderByItem } from "@/features/types";
import { toTitle } from "@/utils/case";
import { useTranslation } from "react-i18next";
import useTypedSearchParams from "@/features/invoices/hooks/use-typed-search-params";
import { PersistedOrderSheetSetListQuery } from "@/features/order-status/agency-order-sheet-set-list-page";
import PageHeader from "@/shared/components/PageHeader";
import SVG from "react-inlinesvg";
import ChipList from "@/features/order-sheet-sets/ChipList";
import { useBuyerOrderSheetSetChipList } from "@/features/order-status/hooks/use-buyer-order-sheet-set-chip-list";
import AppSelect from "@/features/line-sheet-sets/app-select";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import AppOrderDateSelect from "@/shared/components/date-picker/AppOrderDateSelect";
import AppQueryControllerAutoComplete from "@/features/ui/app-query-controller-auto-complete";
import {
  orderStatusOptionList,
  remittanceRequiredStatusOptionList,
} from "@/shared/constants/status";
import {
  convertOrderStatusToNew,
  convertOrderStatusToOld,
} from "@/shared/utils";
import AppInput from "@/features/ui/app-input";
import CustomIcon from "../../../public/icons-v1_1";
import { ListOrderSheetSetsRequest } from "@/features/line-sheet-sets/hooks/use-list-order-sheet-sets";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import CenteredSpinner from "@/shared/components/centered-spinner";
import { Suggestion } from "@/features/ui/app-autocomplete-input";
import useGetCompanies from "@/features/order-sheet-sets/hooks/useGetCompanies";
import { RemittanceRequiredCategory } from "@/features/my-order/features/order-history/types";

interface StatusFilterText {
  label: string;
  value: ListOrderSheetSetsRequest["statusGroup__eq"];
  count?: number;
}

export default function BuyerOrderSheetSetListPage() {
  const identity = useIdentity();
  const [query, setQuery] =
    useTypedSearchParams<PersistedOrderSheetSetListQuery>();
  const { setRows } = useBuyerOrderSheetSetRows();
  const rowsMutateId = useRecoilValue(buyerOrderSheetSetRowsMutateId);
  const { add: addToast, error: showError } = useAppToasts();
  const { t } = useTranslation();
  const { tTitle, tSentence } = useI18nHelper();
  const queryCount = useMemo(() => {
    return Object.keys(query).filter((key) => {
      const value = (query as any)[key];
      if (
        key === "statusGroup__eq" ||
        key === "lineSheetSet.type__in" ||
        key === "lineSheetSet.type__eq" ||
        key === "pageNumber" ||
        key === "query"
      )
        return false;
      if (value === undefined || value === null) return false;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    }).length;
  }, [query]);

  const totalBrandList = useGetCompanies(["BRAND"]) ?? [];
  const totalBoutiqueList = useGetCompanies(["BOUTIQUE"]) ?? [];

  const toast = useToast();
  let inputValue: Suggestion;
  if (query?.query) {
    inputValue = {
      value: query.query,
      type: "custom",
      label: query.query,
    };
  } else {
    inputValue = {
      value: "",
      type: "custom",
      label: "",
    };
  }

  let suggestions: Suggestion[] = totalBrandList.map((brand) => ({
    type: "Brand",
    label: brand.name,
    value: brand.id.toString(),
  }));
  suggestions = suggestions.concat(
    totalBoutiqueList.map((boutique) => ({
      type: "Boutique",
      label: boutique.name,
      value: boutique.id.toString(),
    }))
  );

  const listOrderSheetSetsKey = useMemo(() => {
    if (identity?.company) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request: {
          ...query,
          pageNumber: query.pageNumber || 1,
          pageSize: DEFAULT_LIST_LINE_SHEET_PAIRS_PAGE_SIZE,
          orderBy: [new OrderByItem("submittedAt", "descend")],
        },
      };
    }
  }, [identity, query]);

  const {
    isLoading: isListOrderSheetSetsLoading,
    data: listOrderSheetSetsData,
    error: listOrderSheetSetsError,
    mutate: mutateListOrderSheetSets,
    isValidating: isListOrderSheetSetsValidating,
  } = useListDenseOrderSheetSet(
    listOrderSheetSetsKey?.parameter,
    listOrderSheetSetsKey?.request,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    setRows(listOrderSheetSetsData?.orderSheetSets);
  }, [listOrderSheetSetsData, setRows]);

  useEffect(() => {
    if (rowsMutateId > 0) {
      mutateListOrderSheetSets();
    }
  }, [mutateListOrderSheetSets, rowsMutateId]);

  const statusFilterTextList: StatusFilterText[] = [
    {
      label: tTitle("order_history:all"),
      value: "ALL",
      count: listOrderSheetSetsData?.statistics?.totalCount ?? 0,
    },
    {
      label: tTitle("order_history:order_submitted"),
      value: "ORDER_SUBMITTED",
      count: listOrderSheetSetsData?.statistics?.ORDERED_TO_AGENCY ?? 0,
    },
    {
      label: tTitle("order_history:order_consulting"),
      value: "ORDER_PINGPONG",
      count:
        (listOrderSheetSetsData?.statistics?.ORDERED_TO_BOUTIQUE ?? 0) +
        (listOrderSheetSetsData?.statistics?.AGENCY_CONFIRMED ?? 0) +
        (listOrderSheetSetsData?.statistics?.RESUBMIT_REQUIRED ?? 0) +
        (listOrderSheetSetsData?.statistics?.BOUTIQUE_CONFIRMED ?? 0) +
        (listOrderSheetSetsData?.statistics?.RESUBMIT_COMPLETED ?? 0),
    },
    {
      label: tTitle("order_history:order_confirmed"),
      value: "ORDER_CONFIRMED",
      count: listOrderSheetSetsData?.statistics?.BUYER_CONFIRMED ?? 0,
    },
    {
      label: tTitle("order_history:transportation_ing"),
      value: "TRANSPORTATION_ING",
      count: listOrderSheetSetsData?.statistics?.TRANSPORTATION_ING ?? 0,
    },
    {
      label: tTitle("order_history:transportation_completed"),
      value: "TRANSPORTATION_COMPLETED",
      count: listOrderSheetSetsData?.statistics?.TRANSPORTATION_COMPLETED ?? 0,
    },
    {
      label: tTitle("order_history:completed"),
      value: "COMPLETED",
      count: listOrderSheetSetsData?.statistics?.COMPLETED ?? 0,
    },
    {
      label: tTitle("order_history:order_cancelled"),
      value: "ORDER_CANCELLED",
      count: listOrderSheetSetsData?.statistics?.CANCELED ?? 0,
    },
  ];

  const getIsCurrentStatusMatch = (item: StatusFilterText) => {
    return query.statusGroup__eq === item.value;
  };

  const getFilteredOrderStatusOptionList = () => {
    const currentStatusGroup = query.statusGroup__eq;
    const refinedOrderStatusOptionList = orderStatusOptionList.filter(
      (option) =>
        option.value !== "WORKING_DRAFT" &&
        option.value !== "AGENCY_CONFIRMED" &&
        option.value !== "MODIFICATION_REQUIRED" &&
        option.value !== "MODIFICATION_COMPLETED"
    );

    if (currentStatusGroup === "ALL") return refinedOrderStatusOptionList;
    if (currentStatusGroup === "ORDER_PINGPONG") {
      return refinedOrderStatusOptionList.filter(
        (option) =>
          option.value === "ORDER_CONSULTING" ||
          option.value === "BUYER_MODIFICATION_REQUIRED" ||
          option.value === "BUYER_CONFIRM_REQUIRED" ||
          option.value === "RESUBMIT_COMPLETED"
      );
    }

    return [];
  };

  const showRemittanceRequiredFilter =
    query.statusGroup__eq === "ALL" ||
    query.statusGroup__eq === "TRANSPORTATION_ING" ||
    query.statusGroup__eq === "TRANSPORTATION_COMPLETED";

  const isAllEmpty = listOrderSheetSetsData?.statistics?.totalCount === 0;

  return (
    <Box height={"100%"}>
      <Flex
        flexDirection={"column"}
        gap={"12px"}
        height={"100%"}
        paddingBottom={"16px"}
      >
        <PageHeader
          title={tTitle(
            `navigation.buyer.${query[
              "lineSheetSet.type__eq"
            ]?.toLowerCase()}_order_history`
          )}
          paddingY={"10px"}
          textStyle={"heading-sm"}
        />
        {isAllEmpty && <OrderHistoryNoResults />}
        {!isAllEmpty && (
          <>
            <Box marginTop={"-12px"}>
              <Flex paddingX={"16px"} gap={"16px"} background={"#FFFFFF"}>
                {statusFilterTextList.map((item, index) => {
                  const isSelected = getIsCurrentStatusMatch(item);

                  return (
                    <Box
                      key={index}
                      paddingY={"12px"}
                      cursor={"pointer"}
                      boxShadow={
                        isSelected ? "inset 0 -2px 0 0 #444444" : "none"
                      }
                      onClick={() =>
                        setQuery((prev) => ({
                          ...prev,
                          statusGroup__eq: item.value,
                          status__in: undefined,
                          pageNumber: 1,
                        }))
                      }
                      position={"relative"}
                      _hover={{
                        "&::before": {
                          content: '""',
                          background: "#363636",
                          opacity: 0.04,
                          position: "absolute",
                          inset: 0,
                        },
                      }}
                      _active={{
                        "&::before": {
                          content: '""',
                          background: "#363636",
                          opacity: 0.12,
                          position: "absolute",
                          inset: 0,
                        },
                      }}
                    >
                      <Text
                        textStyle={
                          isSelected ? "body-lg-semi-bold" : "body-lg-medium"
                        }
                        color={isSelected ? "#444444" : "#6F6F6C"}
                        whiteSpace={"nowrap"}
                      >
                        {item.label} {item.count}
                      </Text>
                    </Box>
                  );
                })}
              </Flex>
              <AppListHeader
                filters={[
                  <AppQueryControllerAutoComplete
                    key={"AgencyLineSheetSetListFilter_Query"}
                    value={
                      inputValue.type === "custom"
                        ? inputValue
                        : {
                            value: "",
                            type: "custom",
                            label: "",
                          }
                    }
                    showButton={true}
                    placeholder={t("order_history:search_input_placeholder")}
                    leadingIcon="search"
                    showIconsAndBtn={true}
                    width="400px"
                    suggestions={suggestions}
                    onChange={(value) => {
                      if (
                        value.value &&
                        value.type !== "Brand" &&
                        value.type !== "Boutique" &&
                        value.value.length < 3
                      ) {
                        toast({
                          title: "검색어는 3글자 이상 입력해주세요.",
                          status: "error",
                          duration: 3000,
                          isClosable: true,
                          position: "top",
                        });
                        return;
                      }
                      setQuery((prev) => {
                        if (value.type === "Brand") {
                          // 이미 선택된 브랜드일 경우 무시
                          if (
                            prev["madeByCompany.id__in"]
                              ?.map((id) => String(id))
                              .includes(value.value)
                          ) {
                            return prev;
                          }
                          return {
                            ...prev,
                            "made_by_company.id__in": value.value,
                            query: "",
                            pageNumber: 1,
                          };
                        } else if (value.type === "Boutique") {
                          // 이미 선택된 경우 무시
                          if (
                            prev["lineSheetSet.issuedByCompany.id__in"]
                              ?.map((id) => String(id))
                              .includes(value.value)
                          ) {
                            return prev;
                          }
                          return {
                            ...prev,
                            "line_sheet_set.issued_by_company.id__in":
                              value.value,
                            query: "",
                            pageNumber: 1,
                          };
                        } else {
                          return {
                            ...prev,
                            query: value.value,
                            pageNumber: 1,
                          };
                        }
                      });
                    }}
                  />,
                  // <AppLineSheetTypeSelector
                  //   key={"types"}
                  //   value={query["lineSheetSet.type__in"]}
                  //   width={"80px"}
                  //   isMultiple={true}
                  //   onSelect={(value) =>
                  //     setQuery((prev) => ({
                  //       ...prev,
                  //       "lineSheetSet.type__in": value,
                  //       pageNumber: 1,
                  //     }))
                  //   }
                  //   showCount={true}
                  //   showSelectedBorder={true}
                  //   showEmptyDataOption={true}
                  //   isClearable={false}
                  //   isOrderHistory={true}
                  //   minWidth={"fit-content"}
                  // />,
                  <AppCompanySelect
                    key={"brand"}
                    name={tTitle("brand")}
                    types={["BRAND"]}
                    width={"85px"}
                    id={query["madeByCompany.id__in"]}
                    minWidth={"fit-content"}
                    isMultiple={true}
                    isClearable={false}
                    showEmptyDataOption={true}
                    showSelectedBorder={true}
                    showCount={true}
                    onSelect={(value) =>
                      setQuery((prev) => ({
                        ...prev,
                        "madeByCompany.id__in": value?.map((item) => item.id),
                        pageNumber: 1,
                      }))
                    }
                  />,
                  <AppCompanySelect
                    key={"AgencyOrderSheetSetListFilter_IssuedBy"}
                    name={toTitle(t("common:supplier"))}
                    types={["BOUTIQUE"]}
                    width={"85px"}
                    minWidth={"fit-content"}
                    id={query["lineSheetSet.issuedByCompany.id__in"]}
                    isMultiple={true}
                    isClearable={false}
                    showEmptyDataOption={true}
                    showSelectedBorder={true}
                    showCount={true}
                    onSelect={(company) =>
                      setQuery((prev) => ({
                        ...prev,
                        "lineSheetSet.issuedByCompany.id__in": company?.map(
                          (item) => item.id
                        ),
                        pageNumber: 1,
                      }))
                    }
                  />,
                  <AppOrderDateSelect
                    key={"order-date"}
                    name={tTitle("order_history:order_date")}
                    minWidth={"fit-content"}
                    value={query.period}
                    onSelect={(period) =>
                      setQuery((prev) => ({
                        ...prev,
                        period,
                        pageNumber: 1,
                      }))
                    }
                  />,
                  <Fragment key={"status"}>
                    {getFilteredOrderStatusOptionList().length !== 0 && (
                      <AppSelect<string>
                        minWidth={"fit-content"}
                        name={tTitle("status")}
                        options={getFilteredOrderStatusOptionList().map(
                          (option) => ({
                            ...option,
                            name: tTitle(`order_history:${option.name}`),
                          })
                        )}
                        value={query["status__in"]?.map((status) =>
                          convertOrderStatusToNew(status)
                        )}
                        isMultiple={true}
                        isSearchable={false}
                        isClearable={false}
                        showEmptyDataOption={true}
                        showSelectedBorder={true}
                        showCount={true}
                        onSelect={(value) =>
                          setQuery((prev) => ({
                            ...prev,
                            status__in: value?.map((status) =>
                              convertOrderStatusToOld(status)
                            ),
                            pageNumber: 1,
                          }))
                        }
                      />
                    )}
                  </Fragment>,
                  <Fragment key={"remittanceRequired"}>
                    {showRemittanceRequiredFilter && (
                      <AppSelect<RemittanceRequiredCategory>
                        minWidth={"fit-content"}
                        name={tTitle("remittance_status.remittance_required")}
                        options={remittanceRequiredStatusOptionList.map(
                          (option) => ({
                            ...option,
                            name: tTitle(
                              `order_history:invoice.remittance_required_category.${option.name}`
                            ),
                          })
                        )}
                        value={query["unremittedPaymentType__in"]}
                        isMultiple={true}
                        isSearchable={false}
                        isClearable={false}
                        showEmptyDataOption={true}
                        showSelectedBorder={true}
                        showCount={true}
                        onSelect={(value) =>
                          setQuery((prev) => ({
                            ...prev,
                            unremittedPaymentType__in: value,
                            pageNumber: 1,
                          }))
                        }
                      />
                    )}
                  </Fragment>,
                  // <FormControl
                  //   key={"RemittanceRequiredOnly"}
                  //   display="flex"
                  //   alignItems="center"
                  //   width={"fit-content"}
                  // >
                  //   <FormLabel
                  //     htmlFor="remittance-required-only"
                  //     mb="0"
                  //     mr={"6px"}
                  //   >
                  //     <Text
                  //       textStyle={"body-sm-regular"}
                  //       color={"#444444"}
                  //       whiteSpace={"nowrap"}
                  //     >
                  //       {tSentence("view_remittance_required_only")}
                  //     </Text>
                  //   </FormLabel>
                  //   <Switch
                  //     id="remittance-required-only"
                  //     isChecked={true}
                  //     onChange={(event) => {
                  //       const checked = event.target.checked;
                  //       setQuery((prev) => ({
                  //         ...prev,
                  //         "orderSheetSet.status__in": checked
                  //           ? ["WORKING_DRAFT"]
                  //           : undefined,
                  //         pageNumber: 1,
                  //       }));
                  //     }}
                  //   />
                  // </FormControl>,
                  // <AppButton
                  //   key={"AgencyOrderSheetSetListFilter_ResetFilter"}
                  //   variant={"normal"}
                  //   size={"medium"}
                  //   width={"144px"}
                  //   onClick={() => {
                  //     setQuery((prev) => {
                  //       return {
                  //         pageNumber: 1,
                  //       };
                  //     });
                  //   }}
                  // >
                  //   {toTitle(t("reset_filter"))}
                  // </AppButton>,
                  () => {
                    if (queryCount <= 0) return;
                    return (
                      <AppButton
                        key={"AgencyLineSheetSetListFilter_ResetFilter"}
                        variant={"ghost"}
                        size={"medium"}
                        onClick={() => {
                          setQuery({
                            statusGroup__eq: query.statusGroup__eq,
                            "lineSheetSet.type__eq":
                              query["lineSheetSet.type__eq"],
                            pageNumber: 1,
                          });
                        }}
                        width={"72px"}
                        height={"28px"}
                      >
                        <Flex gap={"4px"} alignItems={"center"}>
                          <SVG
                            src={"/icons-v1_1/arrow-rotate.svg"}
                            width={"16px"}
                            height={"16px"}
                            color={"#8F8F8C"}
                          />
                          <Text textStyle="body-sm-bold" color={"#444444"}>
                            {t("common:clear_all")}
                          </Text>
                        </Flex>
                      </AppButton>
                    );
                  },
                  () => {
                    if (queryCount <= 0) return;
                    return (
                      <Text key={"Divider"} color={"#D4D4D1"}>
                        |
                      </Text>
                    );
                  },
                  <ChipList
                    key={"ChipList"}
                    query={query}
                    setQuery={setQuery}
                    useCustomChipList={useBuyerOrderSheetSetChipList}
                  />,
                ]}
                actions={[]}
                showFilterText={false}
              />
            </Box>
            <Box
              flexGrow={1}
              overflow={"auto"}
              style={{
                scrollbarGutter: "stable",
              }}
            >
              {isListOrderSheetSetsValidating ? (
                <Flex
                  justifyContent={"center"}
                  alignItems={"center"}
                  height={"100%"}
                >
                  <CenteredSpinner />
                </Flex>
              ) : (
                <BuyerOrderSheetSetGrid />
              )}
            </Box>
            <Flex flexDirection={"row"} justifyContent={"center"}>
              <AppPagination
                capacity={10}
                pageSize={DEFAULT_LIST_LINE_SHEET_PAIRS_PAGE_SIZE}
                totalCount={listOrderSheetSetsData?.totalCount}
                pageNumber={query.pageNumber}
                setPageNumber={(value) =>
                  setQuery((prev) => ({ pageNumber: value }))
                }
              />
            </Flex>
          </>
        )}
      </Flex>
    </Box>
  );
}

const OrderHistoryNoResults = () => {
  const { tSentence, tTitle } = useI18nHelper();
  const { navigate } = useRouterHelper();

  return (
    <Flex
      width={"100%"}
      height={"80dvh"}
      justifyContent={"center"}
      alignItems={"center"}
      pointerEvents={"auto"}
    >
      <Flex flexDirection={"column"} alignItems={"center"} gap={"16px"}>
        <CustomIcon
          src={"warning-triangle-solid"}
          width={48}
          height={48}
          color={"#D4D4D1"}
        />
        <Flex flexDirection={"column"} alignItems={"center"} gap={"4px"}>
          <Text color={"#444444"}>
            {tSentence("order_history:no_result_msg.all_description1")}
          </Text>
          <Text>
            {tSentence("order_history:no_result_msg.all_description2")}
          </Text>
        </Flex>
        <Flex gap={"12px"}>
          <AppButton
            size={"medium"}
            onClick={() => navigate("/line-sheets?type__eq=STOCK")}
            width={"120px"}
            height={"28px"}
          >
            <Text textStyle="body-sm-bold" color={"#444444"}>
              {tTitle("order_history:no_result_msg.show_stock")}
            </Text>
          </AppButton>
          <AppButton
            size={"medium"}
            onClick={() => navigate("/line-sheets?type__eq=SEASON")}
            width={"120px"}
            height={"28px"}
          >
            <Text textStyle="body-sm-bold" color={"#444444"}>
              {tTitle("order_history:no_result_msg.show_season")}
            </Text>
          </AppButton>
          {/* 단품오더 보기 버튼은 뺐다 — 라우트가 없다(pages/index.tsx). */}
        </Flex>
      </Flex>
    </Flex>
  );
};

// const OrderHistorySearchInput = () => {
//   const [query, setQuery] =
//     useTypedSearchParams<PersistedOrderSheetSetListQuery>();
//   const [inputVal, setInputVal] = useState<string>(query.query ?? "");
//   const { tSentence } = useI18nHelper();
//
//   return (
//     <AppInput
//       placeholder={tSentence("order_history:search_placeholder")}
//       isClearable={true}
//       value={inputVal}
//       width={"318px"}
//       onChange={(v) => {
//         const next = v ?? "";
//         setInputVal(next);
//
//         if (next === "") {
//           setQuery((prev) => ({
//             ...prev,
//             query: undefined,
//             pageNumber: 1,
//           }));
//         }
//       }}
//       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
//         if (e.key === "Enter") {
//           e.preventDefault();
//           setQuery((prev) => ({
//             ...prev,
//             query: inputVal,
//             pageNumber: 1,
//           }));
//         }
//       }}
//       leftIcon={
//         <CustomIcon src={"search"} width={20} height={20} color={"#8F8F8C"} />
//       }
//     />
//   );
// };